import DOMPurify from 'dompurify'

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe elements
 * like links, buttons, SVGs, and styling that are commonly used in email content
 *
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeHTML = (html) => {
  if (!html) return ''

  // Configure DOMPurify to allow safe HTML elements commonly used in emails
  const config = {
    // Allow safe HTML tags
    ALLOWED_TAGS: [
      'a', 'b', 'i', 'em', 'strong', 'u', 'br', 'p', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'img', 'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g', 'defs', 'clipPath',
      'small', 'button'
    ],

    // Allow safe attributes
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'class', 'style', 'id', 'title', 'alt', 'src', 'width', 'height',
      'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'd',
      'cx', 'cy', 'r', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'points', 'transform'
    ],

    // Allow data attributes (but be careful)
    ALLOW_DATA_ATTR: false,

    // Keep relative URLs (for internal links)
    ALLOW_UNKNOWN_PROTOCOLS: false,

    // Sanitize style attributes (removes JavaScript)
    SANITIZE_STYLE: true,

    // Return sanitized string
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false
  }

  return DOMPurify.sanitize(html, config)
}

/**
 * Helper component for safely rendering HTML content
 * Use this instead of dangerouslySetInnerHTML
 */
export const SafeHTML = ({ html, className = '' }) => {
  const sanitizedHTML = sanitizeHTML(html)

  // html has been run through DOMPurify by sanitizeHTML above
  return ( // nosemgrep: eslint.react-dangerouslysetinnerhtml
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  )
}
